import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { Alert, Box, Snackbar } from "@mui/material";

import { load as load_Simple } from "../actions/dataSimple";

import ModalConfirmation from "../components/ModalConfirmation";
import EventsCalendar from "../components/events/EventsCalendar";
import EventsList from "../components/events/EventsList";
import EventView from "../components/events/EventView";
import EventCreateForm from "../components/events/EventCreateForm";
import HotelSelectorMany from "../components/HotelSelectorMany";

import {
    requestDelete,
    requestBulkDelete,
    requestBulkDuplicate,
    buildBulkDeletePayload,
    fetchEventsBundle,
} from "../helpers/events";
import { ARO_ADMIN, ARO_ALL, GBH_ID, SanitizeHotelID } from "../configuration";
import { dayjsUTC } from "../helpers/dates";

const dayjs = dayjsUTC;

const EventsScreen = () => {
    const { mode } = useParams();
    const navigate = useNavigate();
    const handleToggle = (target) => navigate(`/events/${target}`);

    const { id: hotelID } = useSelector((state) => state.hotelID);
    const { dataSimple } = useSelector((state) => state);
    const { auth } = useSelector((state) => state);
    const authRoles = useSelector((state) => state.authRoles?.edited ?? []);
    const headers = useMemo(
        () => ({ Authorization: `Bearer ${auth.user.jwt}` }),
        [auth],
    );
    const dispatch = useDispatch();
    const dataSimpleRef = useRef(dataSimple.data);

    const [events, setEvents] = useState([]);
    const [eventsExtra, setEventsExtra] = useState({});
    const [loading, setLoading] = useState(false);
    const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState({});

    const [alert, setAlert] = useState({});
    const [alertOpen, setAlertOpen] = useState(false);

    const [openEventForm, setOpenEventForm] = useState(false);
    const [openViewEvent, setOpenViewEvent] = useState(false);
    const [eventClicked, setEventClicked] = useState({});
    const [eventClickedIsCopy, setEventClickedIsCopy] = useState(false);

    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [eventToDuplicate, setEventToDuplicate] = useState(null);
    const [duplicateHotels, setDuplicateHotels] = useState([]);
    const duplicateOnCompleteRef = useRef(null);

    const isAdmin = useMemo(
        () => ARO_ALL.some((role) => authRoles.includes(role)),
        [authRoles],
    );

    useEffect(() => {
        dataSimpleRef.current = dataSimple.data;
    }, [dataSimple.data]);

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        const sanitizedID = SanitizeHotelID(hotelID, GBH_ID);
        fetchEventsBundle(sanitizedID, headers)
            .then((bundle) => {
                let dataToStore = { ...(dataSimpleRef.current ?? {}) };
                dataToStore["Events"] = bundle.Events;
                dispatch(load_Simple(dataToStore, hotelID));
                setEvents(bundle.Events.data);
                setEventsExtra({
                    Contacts: bundle.Events.contacts,
                    Users: bundle.Events.users,
                });
            })
            .catch((err) => {
                console.error("Error fetching events:", err);
                setAlertOpen(true);
                setAlert({
                    message:
                        "Failed to load events. Please try again later or contact with the support team if this persists.",
                    severity: "error",
                });
                setEvents([]);
                setEventsExtra({});
            })
            .finally(() => setLoading(false));
    }, [headers, hotelID, dispatch]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleUpdate = ({
        update = true,
        alert = { message: "Event was created.", severity: "success" },
    }) => {
        if (update) fetchEvents();
        if (openViewEvent) setOpenViewEvent(false);
        setAlertOpen(true);
        setAlert(alert);
    };

    const handleDelete = (event) => {
        let title = "Delete Event";
        let subtitle = `${new Date(event["FromDate"])
            .toUTCString()
            .slice(0, 16)}\n${event.Name}`;
        let description =
            "Are you sure you want to delete this event? This cannot be undone.";
        let answers = [];

        if (event.RepeatRule) {
            let eventID = event.RecurrenceID ?? event.EventID;
            answers = answers.concat([
                {
                    title: "Only this",
                    onSelect: () => {
                        deleteEvent({
                            eventID,
                            choice: "single",
                            exdate: dayjs(event.FromDate).format("YYYY-MM-DD"),
                        });
                    },
                    severity: 1,
                },
                {
                    title: "This and future",
                    onSelect: () => {
                        deleteEvent({
                            eventID,
                            choice: "future",
                            exdate: dayjs(event.FromDate).format("YYYY-MM-DD"),
                        });
                    },
                    severity: 2,
                },
                {
                    title: "Entire series",
                    onSelect: () => {
                        deleteEvent({ eventID, choice: "series" });
                    },
                    severity: 3,
                },
            ]);
        } else {
            answers.push({
                title: "Delete",
                onSelect: () => {
                    deleteEvent({ eventID: event.EventID });
                },
            });
        }

        setConfirmationModal({ title, subtitle, description, answers });
        setOpenConfirmationModal(true);

        const deleteEvent = async ({ eventID, choice = "", exdate = null }) => {
            try {
                const response = await requestDelete(
                    { eventID, choice, exdate },
                    headers,
                );

                if (response.status === 200)
                    handleUpdate({
                        update: true,
                        alert: {
                            message: "Deleted successfully.",
                            severity: "success",
                        },
                    });
            } catch (err) {
                console.error("Error deleting event.", { err });
                handleUpdate({
                    update: false,
                    alert: {
                        message: "Error deleting event.",
                        severity: "error",
                    },
                });
            }
        };
    };

    const handleBulkDuplicate = (selectedEvents, { onComplete } = {}) => {
        if (selectedEvents.length !== 1) return;
        setEventToDuplicate(selectedEvents[0]);
        setDuplicateHotels([]);
        duplicateOnCompleteRef.current = onComplete ?? null;
        setDuplicateDialogOpen(true);
    };

    const handleDuplicateSave = async (hotelsOverride) => {
        const hotels = hotelsOverride ?? duplicateHotels;
        if (!eventToDuplicate || hotels.length === 0) return;
        // Sanitize: replace "default" with the real GBH hotel ID
        const sanitizedHotels = hotels.map((id) => SanitizeHotelID(id, GBH_ID));
        // Exclude the source hotel to avoid duplicating onto itself
        const targetHotels = sanitizedHotels.filter(
            (id) => id !== eventToDuplicate.HotelID,
        );
        if (targetHotels.length === 0) return;
        try {
            const response = await requestBulkDuplicate(
                eventToDuplicate,
                targetHotels,
                headers,
            );
            const failed = response.data?.failed ?? [];
            const failedIds = failed.map((f) => f.hotel_id);
            const succeededCount = targetHotels.length - failedIds.length;
            if (failedIds.length === 0) {
                setDuplicateDialogOpen(false);
                setDuplicateHotels([]);
                duplicateOnCompleteRef.current?.({ failedEventIDs: [] });
            } else {
                setDuplicateHotels(failedIds);
            }
            handleUpdate({
                update: succeededCount > 0,
                alert:
                    failedIds.length === 0
                        ? {
                              message: `Event duplicated to ${targetHotels.length} hotel(s).`,
                              severity: "success",
                          }
                        : {
                              message: `Duplicated to ${succeededCount} hotel(s). ${failedIds.length} failed — retry below.`,
                              severity: "warning",
                          },
            });
        } catch (err) {
            console.error("Error duplicating event:", err);
            handleUpdate({
                update: false,
                alert: {
                    message: "Error duplicating event.",
                    severity: "error",
                },
            });
        }
    };

    const handleBulkDelete = (selectedEvents, { onComplete } = {}) => {
        if (selectedEvents.length === 0) return;

        let title = "Delete Multiple Events";
        let subtitle = `${selectedEvents.length} event${selectedEvents.length > 1 ? "s" : ""} selected`;
        let description =
            "Are you sure you want to delete these events? This cannot be undone.";
        let answers = [
            {
                title: "Cancel",
                onSelect: () => {},
                severity: 0,
            },
            {
                title: "Delete",
                onSelect: () => {
                    deleteBulkEvents(selectedEvents);
                },
                severity: 3,
            },
        ];

        setConfirmationModal({ title, subtitle, description, answers });
        setOpenConfirmationModal(true);

        const deleteBulkEvents = async (eventsToDelete) => {
            try {
                const payload = buildBulkDeletePayload(eventsToDelete);
                const response = await requestBulkDelete(payload, headers);
                const deletedIds = new Set(response.data?.deleted_ids ?? []);
                const failedIds = new Set(response.data?.failed_ids ?? []);

                const failedEvents = eventsToDelete.filter((event) => {
                    const id = Boolean(event.RepeatRule)
                        ? (event.RecurrenceID ?? event.EventID)
                        : event.EventID;
                    return failedIds.has(id);
                });
                const deletedCount = eventsToDelete.filter((event) => {
                    const id = Boolean(event.RepeatRule)
                        ? (event.RecurrenceID ?? event.EventID)
                        : event.EventID;
                    return deletedIds.has(id);
                }).length;

                if (onComplete) {
                    onComplete({ failedEvents });
                }

                if (deletedCount === eventsToDelete.length) {
                    handleUpdate({
                        update: true,
                        alert: {
                            message: `Successfully deleted ${eventsToDelete.length} event(s).`,
                            severity: "success",
                        },
                    });
                    return;
                }

                if (deletedCount > 0) {
                    handleUpdate({
                        update: true,
                        alert: {
                            message: `Deleted ${deletedCount} of ${eventsToDelete.length} event(s). ${failedEvents.length} could not be deleted and remain selected.`,
                            severity: "warning",
                        },
                    });
                    return;
                }

                handleUpdate({
                    update: false,
                    alert: {
                        message: "Error deleting events.",
                        severity: "error",
                    },
                });
            } catch (err) {
                console.error("Error deleting events in bulk.", { err });
                if (onComplete) {
                    onComplete({
                        failedEvents: eventsToDelete,
                    });
                }
                handleUpdate({
                    update: false,
                    alert: {
                        message: "Error deleting events.",
                        severity: "error",
                    },
                });
            }
        };
    };

    const handleEventClicked = (event, open = "view") => {
        setEventClicked(event);

        if (open === "view") setOpenViewEvent(true);
        else if (open === "edit") setOpenEventForm(true);
        else if (open === "copy") {
            const auxEvent = { ...event };
            auxEvent.Name = auxEvent.Name + " (Copy)";
            setEventClicked(auxEvent);
            setEventClickedIsCopy(true);
            setOpenEventForm(true);
        }
    };

    return (
        <>
            {openEventForm && (
                <EventCreateForm
                    eventDetails={eventClicked ?? {}}
                    copiedEvent={eventClickedIsCopy}
                    open={openEventForm}
                    extraData={eventsExtra}
                    onClose={() => {
                        setOpenEventForm(false);
                        setEventClickedIsCopy(false);
                        if (!openViewEvent) setEventClicked({});
                    }}
                    handleUpdate={handleUpdate}
                />
            )}
            {!openEventForm && mode === "calendar" && (
                <EventsCalendar
                    hotelID={hotelID}
                    events={events}
                    onEventClicked={handleEventClicked}
                    onAddNew={(date = null) => {
                        setOpenEventForm(true);
                        if (date) {
                            setEventClickedIsCopy(false);
                            setEventClicked({ FromDate: date, ToDate: date });
                        }
                    }}
                    onToggle={handleToggle}
                />
            )}
            <Box
                sx={{
                    display:
                        openEventForm || mode === "calendar" ? "none" : "block",
                }}
            >
                <EventsList
                    events={events}
                    eventsExtra={eventsExtra}
                    loading={loading}
                    isAdmin={isAdmin}
                    onAddNew={() => setOpenEventForm(true)}
                    onEventClicked={handleEventClicked}
                    onDelete={handleDelete}
                    onBulkDelete={handleBulkDelete}
                    onBulkDuplicate={handleBulkDuplicate}
                    onToggle={handleToggle}
                />
            </Box>
            {Object.keys(eventClicked).length > 0 && (
                <EventView
                    event={eventClicked}
                    extraData={eventsExtra}
                    open={openViewEvent}
                    setOpenEdit={setOpenEventForm}
                    handleClose={() => {
                        setOpenViewEvent(false);
                        setEventClicked({});
                    }}
                    onEdit={
                        mode === "calendar"
                            ? () => {
                                  setOpenViewEvent(false);
                                  setOpenEventForm(true);
                              }
                            : undefined
                    }
                    onDelete={
                        mode === "calendar"
                            ? (ev) => {
                                  setOpenViewEvent(false);
                                  setEventClicked({});
                                  handleDelete(ev);
                              }
                            : undefined
                    }
                />
            )}

            <HotelSelectorMany
                selectedHotels={duplicateHotels}
                setSelectedHotels={setDuplicateHotels}
                open={duplicateDialogOpen}
                onClose={() => setDuplicateDialogOpen(false)}
                onCancel={() => setDuplicateDialogOpen(false)}
                cancelLabel="Cancel"
                onSave={handleDuplicateSave}
                saveLabel={(n) => `Duplicate to ${n} hotel(s)`}
                saveDisabled={(n) => n === 0}
                AccountManager
                disableCurrentHotel
            />
            <Snackbar
                open={alertOpen}
                autoHideDuration={alert.severity === "error" ? null : 4000}
                onClose={() => {
                    setAlertOpen(false);
                    setAlert({});
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={alert.severity}
                    onClose={() => {
                        setAlertOpen(false);
                        setAlert({});
                    }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>

            <ModalConfirmation
                open={openConfirmationModal}
                setOpen={setOpenConfirmationModal}
                title={confirmationModal.title ?? ""}
                description={confirmationModal.description ?? ""}
                subtitle={confirmationModal.subtitle ?? ""}
                answers={confirmationModal.answers ?? []}
                setAnswer={() => {}}
            />
        </>
    );
};

export default EventsScreen;
